import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import { DatesSetArg, EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';
import svLocale from '@fullcalendar/core/locales/sv';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { MeRoutesEnum, RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { Badge, Box } from '@mui/material';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/shared/contexts/appContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Event } from '../models/event';

type Props = {
  data: Event[];
  initialDate: string | undefined;
  loading: boolean;
  dateCallback: (from: string, to: string) => void;
};

export const EventListCalendar = (props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data, loading, dateCallback, initialDate } = props;
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { currentUser, currentMember } = useGlobals();

  const [events, setEvents] = useState<EventInput[]>([]);

  useEffect(() => {
    if (data) {
      // Konvertera varje Event till EventInput format
      const formattedEvents = data.map((event) => {
        return {
          id: String(event.id), // FullCalendar förväntar sig id som en sträng
          title: event.name,
          start: DateHelper.postDateTime(event.date),
          end: undefined,
          isAttending: event.isAttending,
          isFullyBooked: event.isFullyBooked,
          lastApplicationDatePassed: !!(event?.lastDateForApplication && dayjs().isAfter(dayjs(event.lastDateForApplication))),
          // Vi kan lägga till fler egenskaper här
        };
      });
      ConsoleHelper.log('formattedEvents', formattedEvents);
      setEvents(formattedEvents);
    }
  }, [data]);

  const onDatesChanged = (dateInfo: DatesSetArg) => {
    dateCallback(dayjs(dateInfo.startStr).format('YYYY-MM-DD'), dayjs(dateInfo.endStr).subtract(1, 'day').format('YYYY-MM-DD'));
  };

  const eventClickHandler = async (info: EventClickArg) => {
    if (currentMember && !currentUser) {
      navigate(`${MeRoutesEnum.MemberEvents}/${info.event.id}`);
    } else {
      navigate(`${RoutesEnum.Events}/${info.event.id}`);
    }
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    return (
      <Box display='flex' flexDirection='row' justifyContent='start' alignItems='center'>
        <Box mr={2}>{eventInfo.event.title}</Box>
        {eventInfo.event.extendedProps.isAttending && (
          <Box display='flex' flexDirection='row' alignItems='center' height='100%'>
            <Badge
              variant='dot'
              color='success'
              sx={{
                mr: 2,
                '& .MuiBadge-dot': {
                  height: 12,
                  width: 12,
                  borderRadius: 12,
                },
              }}
            />
          </Box>
        )}
        {eventInfo.event.extendedProps.isFullyBooked && (
          <Box display='flex' flexDirection='row' alignItems='center' height='100%'>
            <Badge
              variant='dot'
              color='info'
              sx={{
                mr: 2,
                '& .MuiBadge-dot': {
                  height: 12,
                  width: 12,
                  borderRadius: 12,
                },
              }}
            />
          </Box>
        )}
        {eventInfo.event.extendedProps.lastApplicationDatePassed && (
          <Box display='flex' flexDirection='row' alignItems='center' height='100%'>
            <Badge
              variant='dot'
              color='warning'
              sx={{
                mr: 2,
                '& .MuiBadge-dot': {
                  height: 12,
                  width: 12,
                  borderRadius: 12,
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        // Gör om Idag till en datepicker för månad/år?
        '& .fc': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        '& .fc-list-empty': {
          backgroundColor: theme.palette.background.paper,
        },
        '& .fc-header-toolbar': {
          padding: 2,
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: {
            xs: 'wrap',
            lg: 'nowrap',
          },
        },
        '& .fc-toolbar-title': {
          fontSize: '20px',
        },
        '& .fc-timeline-overlap-disabled, & .fc-timeline-event': {
          borderRadius: '3px',
          m: '3px',
        },

        '& .fc-button-primary': {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          border: `1px solid ${Colors.Form.OutlinedInput}`,
          boxShadow: 'none !important',
        },
        '& .fc-today-button:disabled': {
          backgroundColor: 'transparent',
          color: Colors.Form.OutlinedInput,
          border: `1px solid ${Colors.Form.OutlinedInput}`,
          boxShadow: 'none !important',
        },
        '& .fc .fc-button-primary:not(:disabled):active, & .fc .fc-button-primary:not(:disabled).fc-button-active': {
          boxShadow: 'none !important',
          backgroundColor: theme.palette.primary.main,
          border: 0,
        },
        '& .fc-list-day-text': {
          color: theme.palette.text.primary,
        },
        '& .fc-list-day-side-text': {
          color: theme.palette.text.primary,
        },
        '& .fc-list-event-dot': {
          display: 'none',
        },
        '& .fc-event': {
          cursor: 'pointer',
          position: 'relative',
        },
        '& .fc-list-event-time': {
          pr: 0,
        },
        '& .fc-list-event-graphic': {
          display: 'none',
        },
        '& .fc-list-event-title': {
          color: Colors.LinkColor,
          fontWeight: 400,
          textDecoration: 'underline',
        },
        '& .badge': {
          // position: 'absolute',
          // bottom: '5px',
          // right: '5px',
          display: 'inline-block',
          marginLeft: '10px',
        },
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[listPlugin]}
        locale={i18n.language === 'sv' ? svLocale : undefined}
        initialView='listMonth'
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'listWeek,listMonth',
        }}
        firstDay={1} // Vecka börjar med måndag
        editable={false}
        buttonText={{
          listWeek: t('event:fullCalendar.week'),
          listMonth: t('event:fullCalendar.month'),
          today: t('event:fullCalendar.today'),
        }}
        nowIndicator
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit' }}
        initialDate={initialDate ?? dayjs().format('YYYY-MM-DD')}
        events={events ?? []}
        datesSet={onDatesChanged}
        eventClick={(eventInfo) => eventClickHandler(eventInfo)}
        height='auto'
        eventContent={renderEventContent}
      />
    </Box>
  );
};
